import type { IVector2 } from '@seddi/math-v2';

// Meters to pixels
export const SIZE_MULTIPLIER = 5e3;

export const REGION_LINE_WIDTH = 8;
export const REGION_MARGING = 5;

export const regionTextureSizeNumbers = (size: IVector2, lineWidth: number) => {
  const sizeIncreaseInPixels = 2 * (lineWidth + REGION_MARGING);
  const originalSizeInPixels = {
    x: size.x * SIZE_MULTIPLIER,
    y: size.y * SIZE_MULTIPLIER,
  };
  return {
    metersSize: {
      x: size.x * (1 + sizeIncreaseInPixels / originalSizeInPixels.x),
      y: size.y * (1 + sizeIncreaseInPixels / originalSizeInPixels.y),
    },
    pixelsSize: {
      x: originalSizeInPixels.x + sizeIncreaseInPixels,
      y: originalSizeInPixels.y + sizeIncreaseInPixels,
    },
    pixelsOriginalSize: {
      x: originalSizeInPixels.x,
      y: originalSizeInPixels.y,
    },
  };
};

export const regionSizeToLineSizes = (size: IVector2) => {
  const regionBigVersion = size.x >= 0.25 || size.y >= 0.25;

  return regionBigVersion
    ? {
        lineWidth: 8,
        borderLineDashSegments: [24, 16],
        borderLineDashOffset: 12,
        fillLineDashSegments: [20, 20],
        fillLineDashOffset: 10,
      }
    : {
        lineWidth: 4,
        borderLineDashSegments: [12, 8],
        borderLineDashOffset: 6,
        fillLineDashSegments: [10, 10],
        fillLineDashOffset: 5,
      };
};

/**
 * Create logo region procedural texture using canvas.
 * Draws a dashed border around the logo area (not covering the logo).
 * @param size Logo region size (meters).
 */
export const createLogoRegionTexture = (
  size: IVector2,
  styleBorder: string,
  styleFill: string,
): Promise<ImageBitmap> => {
  const lineSizes = regionSizeToLineSizes(size);
  const regionSizes = regionTextureSizeNumbers(size, lineSizes.lineWidth);
  const x = regionSizes.pixelsOriginalSize.x;
  const y = regionSizes.pixelsOriginalSize.y;

  const canvas = document.createElement('canvas');
  canvas.width = regionSizes.pixelsSize.x;
  canvas.height = regionSizes.pixelsSize.y;
  const ctx = canvas.getContext('2d')!;

  // Border.
  ctx.setLineDash(lineSizes.borderLineDashSegments);
  ctx.lineDashOffset = lineSizes.borderLineDashOffset;
  ctx.lineWidth = lineSizes.lineWidth;
  ctx.strokeStyle = styleBorder;
  ctx.strokeRect(
    lineSizes.lineWidth / 2 + REGION_MARGING,
    lineSizes.lineWidth / 2 + REGION_MARGING,
    x + lineSizes.lineWidth,
    y + lineSizes.lineWidth,
  );
  // Fill.
  ctx.setLineDash(lineSizes.fillLineDashSegments);
  ctx.lineDashOffset = lineSizes.fillLineDashOffset;
  ctx.lineWidth = (2 * lineSizes.lineWidth) / 3;
  ctx.strokeStyle = styleFill;
  ctx.strokeRect(
    lineSizes.lineWidth / 2 + REGION_MARGING,
    lineSizes.lineWidth / 2 + REGION_MARGING,
    x + lineSizes.lineWidth,
    y + lineSizes.lineWidth,
  );

  return createImageBitmap(canvas);
};

export const HOVER_BORDER_WIDTH = 5;
export const SELECTED_COLOR = 'rgb(50, 147, 111)';
export const HOVER_COLOR = 'rgb(103, 203, 166)';

/**
 * Calculate hover texture size including border.
 * @param logoSize Logo size in meters.
 * @returns Texture size in meters (logo size + border padding).
 */
export const calculateHoverTextureSize = (logoSize: IVector2): IVector2 => {
  const borderSizeInMeters = HOVER_BORDER_WIDTH / SIZE_MULTIPLIER;
  return {
    x: logoSize.x + borderSizeInMeters * 2,
    y: logoSize.y + borderSizeInMeters * 2,
  };
};

/**
 * Create hover texture using canvas and return ImageBitmap.
 * Shows a border around the logo.
 * @param logoSize Logo size (meters).
 * @param useSelectedColor Use selected color instead of hover color.
 */
export const createHoverTexture = (
  logoSize: IVector2,
  useSelectedColor = false,
): Promise<ImageBitmap> => {
  const logoTextureWidth = Math.ceil(logoSize.x * SIZE_MULTIPLIER);
  const logoTextureHeight = Math.ceil(logoSize.y * SIZE_MULTIPLIER);

  const textureWidth = logoTextureWidth + HOVER_BORDER_WIDTH * 2;
  const textureHeight = logoTextureHeight + HOVER_BORDER_WIDTH * 2;

  const canvas = document.createElement('canvas');
  canvas.width = textureWidth;
  canvas.height = textureHeight;
  const ctx = canvas.getContext('2d')!;

  const strokeColor = useSelectedColor ? SELECTED_COLOR : HOVER_COLOR;

  Object.assign(ctx, {
    imageSmoothingEnabled: false,
    shadowColor: 'rgba(0, 0, 0, 0.25)',
    shadowBlur: 4,
    shadowOffsetX: 2,
    shadowOffsetY: 2,
    strokeStyle: strokeColor,
    lineWidth: HOVER_BORDER_WIDTH,
  });

  ctx.strokeRect(
    HOVER_BORDER_WIDTH / 2,
    HOVER_BORDER_WIDTH / 2,
    logoTextureWidth + HOVER_BORDER_WIDTH,
    logoTextureHeight + HOVER_BORDER_WIDTH,
  );

  return createImageBitmap(canvas);
};
